import { Dimension } from '../../../../../features/dimension/types/Dimension'

export interface DimensionOrderPatch {
  id: number
  order: number
  parentDimensionId: string | null
}

/**
 * Reparents `dimensionIds` under `newParentDimensionId`, appending them (in the given order) after
 * the target parent's existing children, and recomputes `order` for every sibling group touched by
 * the move (each old parent group that loses a node, and the new parent group) — same "recompute the
 * whole affected sibling array" behavior as the old drag-and-drop save(), generalized to N moved nodes.
 */
export const buildReparentBatch = (
  flatList: Dimension[],
  dimensionIds: string[],
  newParentDimensionId: string | null
): DimensionOrderPatch[] => {
  const movedSet = new Set(dimensionIds)

  // Reject moving a node under itself or one of its own descendants (would create a cycle).
  // Callers are expected to pre-filter via useLegalMoveTargets, but this is a defense-in-depth
  // self-check so a caller bug can't silently write a cyclic parentDimensionId graph — buildDropBatch
  // already has the equivalent guard for drag-and-drop.
  if (newParentDimensionId && movedSet.has(newParentDimensionId)) return []
  const childrenByParent = new Map<string, string[]>()
  flatList.forEach(d => {
    if (d.parentDimensionId) {
      if (!childrenByParent.has(d.parentDimensionId)) childrenByParent.set(d.parentDimensionId, [])
      childrenByParent.get(d.parentDimensionId)!.push(d.dimensionId)
    }
  })
  const descendants = new Set<string>()
  const stack = [...dimensionIds]
  while (stack.length) {
    const current = stack.pop()!
    for (const childId of childrenByParent.get(current) || []) {
      if (!descendants.has(childId)) {
        descendants.add(childId)
        stack.push(childId)
      }
    }
  }
  if (newParentDimensionId && descendants.has(newParentDimensionId)) return []

  const byId = new Map(flatList.map(d => [d.dimensionId, d]))

  const siblingsByParent = new Map<string | null, Dimension[]>()
  flatList.forEach(d => {
    const key = d.parentDimensionId ?? null
    if (!siblingsByParent.has(key)) siblingsByParent.set(key, [])
    siblingsByParent.get(key)!.push(d)
  })
  siblingsByParent.forEach(siblings => siblings.sort((a, b) => a.order - b.order))

  const touchedParentKeys = new Set<string | null>()
  siblingsByParent.forEach((siblings, parentKey) => {
    if (siblings.some(s => movedSet.has(s.dimensionId))) touchedParentKeys.add(parentKey)
  })
  touchedParentKeys.add(newParentDimensionId)

  const changed: DimensionOrderPatch[] = []
  touchedParentKeys.forEach(parentKey => {
    const remainingSiblings = (siblingsByParent.get(parentKey) || []).filter(s => !movedSet.has(s.dimensionId))
    const finalSiblings =
      parentKey === newParentDimensionId
        ? [...remainingSiblings, ...dimensionIds.map(id => byId.get(id)).filter((d): d is Dimension => !!d)]
        : remainingSiblings

    finalSiblings.forEach((dimension, index) => {
      changed.push({ id: dimension.id, order: index, parentDimensionId: parentKey })
    })
  })

  return changed
}

export type DropIndicatorPosition = 'above' | 'below' | 'inside'

export interface DropResolution {
  patch: DimensionOrderPatch[]
  indicator: { targetDimensionId: string; position: DropIndicatorPosition } | null
}

/**
 * Resolves a drag-and-drop of `sourceId` relative to `targetId` (the row under the cursor, or null
 * for the root) into an order/parent patch plus the drop-indicator position to show while dragging.
 *
 * - `reorderOnly` (Shift held): stays a sibling of the target, never nests.
 * - otherwise: dropping onto a row that has children nests inside it; dropping onto a leaf makes the
 *   source a sibling right after it.
 * Dropping a node onto itself or into its own subtree is rejected (empty patch).
 */
export const buildDropBatch = (
  flatList: Dimension[],
  sourceId: string,
  targetId: string | null,
  reorderOnly: boolean,
  preferAbove = false
): DropResolution => {
  const empty: DropResolution = { patch: [], indicator: null }
  if (!sourceId || sourceId === targetId) return empty

  const byId = new Map(flatList.map(d => [d.dimensionId, d]))
  const source = byId.get(sourceId)
  if (!source) return empty

  const siblingsByParent = new Map<string | null, Dimension[]>()
  flatList.forEach(d => {
    const key = d.parentDimensionId ?? null
    if (!siblingsByParent.has(key)) siblingsByParent.set(key, [])
    siblingsByParent.get(key)!.push(d)
  })
  siblingsByParent.forEach(siblings => siblings.sort((a, b) => a.order - b.order))

  const childCount = (dimensionId: string) => siblingsByParent.get(dimensionId)?.length ?? 0

  // Descendants of the source — dropping into its own subtree is illegal.
  const descendants = new Set<string>()
  const stack = [sourceId]
  while (stack.length) {
    const current = stack.pop()!
    for (const child of siblingsByParent.get(current) || []) {
      if (!descendants.has(child.dimensionId)) {
        descendants.add(child.dimensionId)
        stack.push(child.dimensionId)
      }
    }
  }

  const target = targetId ? byId.get(targetId) : undefined
  if (targetId && (!target || descendants.has(targetId))) return empty

  const siblingPosition: DropIndicatorPosition = preferAbove ? 'above' : 'below'
  let newParentKey: string | null
  let position: DropIndicatorPosition
  if (!target) {
    newParentKey = null
    position = 'below'
  } else if (reorderOnly) {
    newParentKey = target.parentDimensionId ?? null
    position = siblingPosition
  } else if (childCount(target.dimensionId) > 0) {
    newParentKey = target.dimensionId
    position = 'inside'
  } else {
    newParentKey = target.parentDimensionId ?? null
    position = siblingPosition
  }

  const oldParentKey = source.parentDimensionId ?? null
  const dest = (siblingsByParent.get(newParentKey) || []).filter(d => d.dimensionId !== sourceId)

  let insertIndex = dest.length
  if (position !== 'inside' && target && newParentKey === (target.parentDimensionId ?? null)) {
    const targetIndex = dest.findIndex(d => d.dimensionId === targetId)
    if (targetIndex !== -1) insertIndex = position === 'above' ? targetIndex : targetIndex + 1
  }
  dest.splice(insertIndex, 0, source)

  const patch: DimensionOrderPatch[] = []
  dest.forEach((dimension, index) => patch.push({ id: dimension.id, order: index, parentDimensionId: newParentKey }))
  if (oldParentKey !== newParentKey) {
    ;(siblingsByParent.get(oldParentKey) || [])
      .filter(d => d.dimensionId !== sourceId)
      .forEach((dimension, index) => patch.push({ id: dimension.id, order: index, parentDimensionId: oldParentKey }))
  }

  return {
    patch,
    indicator: target ? { targetDimensionId: target.dimensionId, position } : null
  }
}

/**
 * Swaps `order` between `dimensionId` and its immediate sibling in the given direction.
 * Returns an empty array when there is no sibling to swap with (already first/last).
 */
export const buildReorderBatch = (
  flatList: Dimension[],
  dimensionId: string,
  direction: 'up' | 'down'
): Pick<DimensionOrderPatch, 'id' | 'order'>[] => {
  const dimension = flatList.find(d => d.dimensionId === dimensionId)
  if (!dimension) return []

  const siblings = flatList
    .filter(d => (d.parentDimensionId ?? null) === (dimension.parentDimensionId ?? null))
    .sort((a, b) => a.order - b.order)

  const index = siblings.findIndex(d => d.dimensionId === dimensionId)
  const swapIndex = direction === 'up' ? index - 1 : index + 1
  if (index === -1 || swapIndex < 0 || swapIndex >= siblings.length) return []

  const current = siblings[index]
  const neighbor = siblings[swapIndex]
  return [
    { id: current.id, order: neighbor.order },
    { id: neighbor.id, order: current.order }
  ]
}

/**
 * Moves the whole `dimensionIds` selection up or down by one slot among their shared siblings.
 * A non-contiguous selection is compacted into one contiguous block in the process — a sibling that
 * was interleaved between two selected items ends up pushed to the far side of the block together
 * with the other untouched siblings, even though it wasn't itself selected.
 *
 * Requires every id in `dimensionIds` to share the same parent (returns `[]` otherwise — the caller
 * is expected to disable the action in that case, mirroring how `useLegalMoveTargets` pre-filters
 * illegal reparent targets rather than relying solely on this defense-in-depth check). Also returns
 * `[]` when the selection is already at that edge of the sibling list (nothing to swap past).
 */
export const buildBlockReorderBatch = (
  flatList: Dimension[],
  dimensionIds: string[],
  direction: 'up' | 'down'
): Pick<DimensionOrderPatch, 'id' | 'order'>[] => {
  const selectedSet = new Set(dimensionIds)
  const selected = flatList.filter(d => selectedSet.has(d.dimensionId))
  if (selected.length === 0) return []

  const parentKeys = new Set(selected.map(d => d.parentDimensionId ?? null))
  if (parentKeys.size > 1) return []
  const [parentKey] = parentKeys

  const siblings = flatList.filter(d => (d.parentDimensionId ?? null) === parentKey).sort((a, b) => a.order - b.order)

  const isSelected = (d: Dimension) => selectedSet.has(d.dimensionId)
  const selectedIndices = siblings.reduce<number[]>((acc, d, i) => (isSelected(d) ? [...acc, i] : acc), [])
  const topIndex = selectedIndices[0]
  const bottomIndex = selectedIndices[selectedIndices.length - 1]

  const pivotIndex = direction === 'up' ? topIndex - 1 : bottomIndex + 1
  if (pivotIndex < 0 || pivotIndex >= siblings.length) return []
  const pivot = siblings[pivotIndex]

  const selectedOrdered = siblings.filter(isSelected)
  const withoutSelected = siblings.filter(d => !isSelected(d))
  const pivotIdx = withoutSelected.findIndex(d => d.dimensionId === pivot.dimensionId)

  const finalOrder =
    direction === 'up'
      ? [...withoutSelected.slice(0, pivotIdx), ...selectedOrdered, ...withoutSelected.slice(pivotIdx)]
      : [...withoutSelected.slice(0, pivotIdx), pivot, ...selectedOrdered, ...withoutSelected.slice(pivotIdx + 1)]

  return finalOrder.reduce<Pick<DimensionOrderPatch, 'id' | 'order'>[]>((patch, dimension, index) => {
    if (dimension.order !== index) patch.push({ id: dimension.id, order: index })
    return patch
  }, [])
}

/**
 * Moves the whole `dimensionIds` selection to the very start or end of their shared siblings,
 * compacting a non-contiguous selection into one block — same convention as buildBlockReorderBatch
 * (selected items keep their original relative order to each other; everything else keeps its own
 * relative order too, just pushed to the other side of the block).
 *
 * Requires every id to share the same parent (returns `[]` otherwise). No boundary guard is needed
 * here (unlike buildBlockReorderBatch) — if the selection is already fully at that edge, the diff
 * check below naturally emits an empty patch.
 */
export const buildMoveToEdgeBatch = (
  flatList: Dimension[],
  dimensionIds: string[],
  edge: 'start' | 'end'
): Pick<DimensionOrderPatch, 'id' | 'order'>[] => {
  const selectedSet = new Set(dimensionIds)
  const selected = flatList.filter(d => selectedSet.has(d.dimensionId))
  if (selected.length === 0) return []

  const parentKeys = new Set(selected.map(d => d.parentDimensionId ?? null))
  if (parentKeys.size > 1) return []
  const [parentKey] = parentKeys

  const siblings = flatList.filter(d => (d.parentDimensionId ?? null) === parentKey).sort((a, b) => a.order - b.order)
  const isSelected = (d: Dimension) => selectedSet.has(d.dimensionId)
  const selectedOrdered = siblings.filter(isSelected)
  const withoutSelected = siblings.filter(d => !isSelected(d))

  const finalOrder =
    edge === 'start' ? [...selectedOrdered, ...withoutSelected] : [...withoutSelected, ...selectedOrdered]

  return finalOrder.reduce<Pick<DimensionOrderPatch, 'id' | 'order'>[]>((patch, dimension, index) => {
    if (dimension.order !== index) patch.push({ id: dimension.id, order: index })
    return patch
  }, [])
}
